/**
 * 设备详情页面 - 上网时间统计
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthToken } from '../services/auth';
import { API_BASE_URL } from '../services/api';
import TabBar from '../components/TabBar';
import Loading from '../components/Loading';
import './DeviceDetail.css';

const CATEGORIES = ['全部', '学习', '影音娱乐', '社交资讯', '游戏', '其他'];

const DEFAULT_CATEGORIES = [
  { name: '学习', color: '#4A90D9', seconds: 0 },
  { name: '影音娱乐', color: '#E85D75', seconds: 0 },
  { name: '社交资讯', color: '#9B6DD9', seconds: 0 },
  { name: '游戏', color: '#4CD964', seconds: 0 },
  { name: '其他', color: '#8E8E93', seconds: 0 },
];

const DeviceDetail = () => {
  const { deviceId, mac } = useParams();
  const navigate = useNavigate();
  const [dateTab, setDateTab] = useState('today');
  const [categoryFilter, setCategoryFilter] = useState('全部');
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    totalSeconds: 0,
    categories: DEFAULT_CATEGORIES,
    timeline: [],
    apps: []
  });

  const getDateRange = useCallback((tab) => {
    const today = new Date();
    const formatDate = (d) => d.toISOString().split('T')[0];
    
    if (tab === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { start: formatDate(yesterday), end: formatDate(yesterday) };
    }
    if (tab === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 6);
      return { start: formatDate(weekAgo), end: formatDate(today) };
    }
    return { start: formatDate(today), end: formatDate(today) };
  }, []);

  const fetchAppUsage = useCallback(async (tab) => {
    setLoading(true);
    setError(null);
    
    const { start, end } = getDateRange(tab);
    
    try {
      const response = await fetch(`${API_BASE_URL}/ws/app-usage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAuthToken() || ''}`
        },
        body: JSON.stringify({
          device_id: decodeURIComponent(deviceId),
          mac: decodeURIComponent(mac),
          start_date: start,
          end_date: end
        })
      });
      
      const result = await response.json();
      
      if (result.code === 0 && result.data) {
        setData({
          totalSeconds: result.data.totalSeconds || 0,
          categories: result.data.categories || DEFAULT_CATEGORIES,
          timeline: result.data.timeline || [],
          apps: result.data.apps || []
        });
      } else {
        setError(result.message || '获取数据失败');
      }
    } catch (err) {
      setError('网络请求失败：' + err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, mac, getDateRange]);

  useEffect(() => {
    fetchAppUsage(dateTab);
  }, [dateTab, fetchAppUsage]);

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours} 小时 ${minutes} 分钟` : `${minutes} 分钟`;
  };

  const formatDurationShort = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}小时${minutes}分钟` : `${minutes}分钟`;
  };

  const totalCategorySeconds = data.categories.reduce((sum, c) => sum + c.seconds, 0);
  const filteredApps = categoryFilter === '全部' ? data.apps : data.apps.filter(app => app.category === categoryFilter);
  const sortedTimeline = [...data.timeline].sort((a, b) => b.time.localeCompare(a.time));
  const timelineToShow = timelineExpanded ? sortedTimeline : sortedTimeline.slice(0, 5);

  return (
    <div className="page-container usage-stats">
      <header className="page-header">
        <button className="btn-back" onClick={() => navigate('/games')}>←</button>
        <div className="page-header-title">
          <h1>上网时间统计</h1>
        </div>
        <div style={{ width: 40 }}></div>
      </header>

      <div className="date-tabs">
        {['yesterday', 'today', 'week'].map(tab => (
          <button
            key={tab}
            className={`date-tab ${dateTab === tab ? 'active' : ''}`}
            onClick={() => setDateTab(tab)}
            disabled={loading}
          >
            {tab === 'yesterday' ? '昨日' : tab === 'today' ? '今日' : '过去七天'}
          </button>
        ))}
      </div>

      {loading && <Loading text="正在从设备获取数据..." />}

      {error && !loading && (
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <div className="error-text">{error}</div>
          <button className="btn btn-primary" onClick={() => fetchAppUsage(dateTab)}>重试</button>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="card total-usage-card">
            <div className="total-label">使用总时长</div>
            <div className="total-time">{formatDuration(data.totalSeconds)}</div>
            
            {totalCategorySeconds > 0 && (
              <div className="category-bar">
                {data.categories.map((cat, i) => cat.seconds > 0 && (
                  <div key={i} className="category-segment" style={{ backgroundColor: cat.color, width: `${(cat.seconds / totalCategorySeconds) * 100}%` }} />
                ))}
              </div>
            )}

            <div className="category-legend">
              {data.categories.map((cat, i) => (
                <div key={i} className="legend-item">
                  <span className="legend-dot" style={{ backgroundColor: cat.color }}></span>
                  <span className="legend-name">{cat.name}</span>
                  <span className="legend-time">{formatDurationShort(cat.seconds)}</span>
                </div>
              ))}
            </div>
          </div>

          {dateTab !== 'week' && data.timeline.length > 0 && (
            <div className="card timeline-section">
              <h3 className="section-title">上网足迹</h3>
              <div className="timeline-list">
                {timelineToShow.map((item, i) => (
                  <div key={i} className="timeline-item">
                    <div className="timeline-time">{item.time}</div>
                    <div className="timeline-dot-container">
                      <div className="timeline-dot"></div>
                      {i < timelineToShow.length - 1 && <div className="timeline-line"></div>}
                    </div>
                    <div className="timeline-content">
                      <div className="timeline-app-icon">{item.icon}</div>
                      <div className="timeline-app-name">{item.app}</div>
                      <div className="timeline-duration">{formatDurationShort(item.duration)}</div>
                    </div>
                  </div>
                ))}
              </div>
              {data.timeline.length > 5 && (
                <div className="timeline-toggle">
                  <button className="toggle-btn" onClick={() => setTimelineExpanded(!timelineExpanded)}>
                    {timelineExpanded ? '收起' : '展开'}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="card apps-section">
            <h3 className="section-title">常用应用统计</h3>
            
            <div className="category-filters">
              {CATEGORIES.map((cat, i) => (
                <button
                  key={i}
                  className={`filter-btn ${categoryFilter === cat ? 'active' : ''}`}
                  onClick={() => setCategoryFilter(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="app-list">
              {filteredApps.length > 0 ? (
                filteredApps.map((app, i) => (
                  <div key={i} className="app-item">
                    <div className="app-icon">{app.icon}</div>
                    <div className="app-name">{app.name}</div>
                    <div className="app-duration">{formatDurationShort(app.duration)}</div>
                  </div>
                ))
              ) : (
                <div className="empty-text">暂无数据</div>
              )}
            </div>
          </div>
        </>
      )}

      <TabBar />
    </div>
  );
};

export default DeviceDetail;
